import { useEffect, useState } from 'react';
import { CaretLeft, CaretRight } from 'shared/icons';
import { useTokens } from './tokens';

/**
 * Calendário duplo (lado a lado) pra seleção de range de datas. Usado quando
 * o filtro de período é 'custom'. Sem presets rápidos — esses já estão no
 * menu de período pai (Toolbar/PresetBuilder), aqui o foco é só selecionar
 * duas datas livres clicando direto no calendário.
 *
 * Estilizado com tokens da página /operacoes (T.bg/T.line/T.accent/etc) —
 * adapta a todos os temas. Diferente do DateRangeFilter de /sellout que usa
 * classes Tailwind hardcoded.
 *
 * Props:
 *   from/to ('YYYY-MM-DD' | null) — range atual
 *   onChange(fromStr, toStr) — disparado quando ambas as datas estão selecionadas
 *   onClear() — opcional, limpa o range
 */
// Todas as ops do calendário em UTC puro — picker representa dates abstratas
// (YYYY-MM-DD), não wall-clock do usuário. Coerente com o resto do pipeline.

function parseIso(value) {
    if (!value || typeof value !== 'string') return null;
    const [y, m, d] = value.split('-').map((p) => Number.parseInt(p, 10));
    if (!y || !m || !d) return null;
    return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
}

function toIso(date) {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function toDisplay(date) {
    const dd = String(date.getUTCDate()).padStart(2, '0');
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const yy = date.getUTCFullYear();
    return `${dd}/${mm}/${yy}`;
}

function startOfDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
}

const MONTH_NAMES = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
];
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function DateRangePicker({ from, to, onChange, onClear }) {
    const T = useTokens();
    const [startDate, setStartDate] = useState(() => parseIso(from));
    const [endDate, setEndDate] = useState(() => parseIso(to));
    const [hoverDate, setHoverDate] = useState(null);
    const [leftMonth, setLeftMonth] = useState(() => {
        const seed = parseIso(from) || new Date();
        return new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth(), 1, 0, 0, 0, 0));
    });
    const [rightMonth, setRightMonth] = useState(() => {
        const seed = parseIso(from) || new Date();
        return new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth() + 1, 1, 0, 0, 0, 0));
    });

    // Sync com props externas (caso o caller mude from/to via reset/etc).
    useEffect(() => {
        setStartDate(parseIso(from));
        setEndDate(parseIso(to));
    }, [from, to]);

    const isInRange = (date) => {
        if (!startDate || !endDate) return false;
        const d = startOfDay(date).getTime();
        const s = startOfDay(startDate).getTime();
        const e = startOfDay(endDate).getTime();
        return d >= s && d <= e;
    };
    const isInHoverRange = (date) => {
        if (!startDate || !hoverDate || endDate) return false;
        const a = startDate < hoverDate ? startDate : hoverDate;
        const b = startDate < hoverDate ? hoverDate : startDate;
        const d = startOfDay(date).getTime();
        return d >= startOfDay(a).getTime() && d <= startOfDay(b).getTime();
    };
    const isEdge = (date, edge) => {
        const ref = edge === 'start' ? startDate : endDate;
        if (!ref) return false;
        return startOfDay(date).getTime() === startOfDay(ref).getTime();
    };

    const handleClick = (date) => {
        if (!startDate || (startDate && endDate)) {
            setStartDate(date);
            setEndDate(null);
            return;
        }
        const [a, b] = date < startDate ? [date, startDate] : [startDate, date];
        setStartDate(a);
        setEndDate(b);
        onChange?.(toIso(a), toIso(b));
    };

    const changeMonth = (side, dir) => {
        if (side === 'left') {
            const m = new Date(leftMonth);
            m.setUTCMonth(m.getUTCMonth() + dir);
            setLeftMonth(m);
            if (m.getTime() >= rightMonth.getTime()) {
                const r = new Date(m);
                r.setUTCMonth(r.getUTCMonth() + 1);
                setRightMonth(r);
            }
        } else {
            const m = new Date(rightMonth);
            m.setUTCMonth(m.getUTCMonth() + dir);
            setRightMonth(m);
            if (m.getTime() <= leftMonth.getTime()) {
                const l = new Date(m);
                l.setUTCMonth(l.getUTCMonth() - 1);
                setLeftMonth(l);
            }
        }
    };

    const renderCalendar = (month, side) => {
        const firstDay = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1, 0, 0, 0, 0));
        const gridStart = new Date(firstDay);
        gridStart.setUTCDate(gridStart.getUTCDate() - firstDay.getUTCDay());
        const days = [];
        for (let i = 0; i < 42; i += 1) {
            const d = new Date(gridStart);
            d.setUTCDate(gridStart.getUTCDate() + i);
            days.push(d);
        }
        return (
            <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <button
                        type="button"
                        onClick={() => changeMonth(side, -1)}
                        aria-label="Mês anterior"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: T.muted,
                            padding: 4,
                            display: 'inline-flex',
                            borderRadius: 4,
                            outline: 'none',
                        }}
                    >
                        <CaretLeft size={14} />
                    </button>
                    <span
                        style={{
                            flex: 1,
                            textAlign: 'center',
                            fontSize: 12,
                            fontWeight: 600,
                            color: T.ink,
                        }}
                    >
                        {MONTH_NAMES[month.getUTCMonth()]} {month.getUTCFullYear()}
                    </span>
                    <button
                        type="button"
                        onClick={() => changeMonth(side, 1)}
                        aria-label="Próximo mês"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: T.muted,
                            padding: 4,
                            display: 'inline-flex',
                            borderRadius: 4,
                            outline: 'none',
                        }}
                    >
                        <CaretRight size={14} />
                    </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                    {WEEKDAYS.map((w) => (
                        <div
                            key={w}
                            style={{
                                textAlign: 'center',
                                fontSize: 10,
                                fontWeight: 600,
                                color: T.muted,
                                padding: '4px 0',
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                            }}
                        >
                            {w}
                        </div>
                    ))}
                    {days.map((d, idx) => {
                        const isCurrent = d.getUTCMonth() === month.getUTCMonth();
                        const inRange = isInRange(d);
                        const inHover = isInHoverRange(d);
                        const startEdge = isEdge(d, 'start');
                        const endEdge = isEdge(d, 'end');
                        const isEdgeAny = startEdge || endEdge;
                        const bg = isEdgeAny ? T.accent : inRange || inHover ? T.accentBg : 'transparent';
                        const color = isEdgeAny
                            ? '#fff'
                            : !isCurrent
                              ? T.subtle
                              : inRange || inHover
                                ? T.accent
                                : T.ink;
                        return (
                            <button
                                // biome-ignore lint/suspicious/noArrayIndexKey: dias do grid são determinísticos pelo mês renderizado
                                key={idx}
                                type="button"
                                onClick={() => isCurrent && handleClick(d)}
                                onMouseEnter={() => isCurrent && setHoverDate(d)}
                                onMouseLeave={() => setHoverDate(null)}
                                disabled={!isCurrent}
                                style={{
                                    aspectRatio: '1 / 1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 11,
                                    fontFamily: T.mono,
                                    border: 'none',
                                    cursor: isCurrent ? 'pointer' : 'default',
                                    borderRadius: 3,
                                    background: bg,
                                    color,
                                    fontWeight: isEdgeAny ? 700 : 400,
                                    outline: 'none',
                                    opacity: isCurrent ? 1 : 0.4,
                                }}
                            >
                                {d.getUTCDate()}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const footerText =
        startDate && !endDate
            ? `Início: ${toDisplay(startDate)} — selecione a data final`
            : startDate && endDate
              ? `${toDisplay(startDate)} → ${toDisplay(endDate)}`
              : 'Clique pra escolher data inicial';

    return (
        <div
            data-faid="Date_Range_Picker_Operacoes"
            style={{
                background: T.bg,
                border: `1px solid ${T.line}`,
                borderRadius: 6,
                padding: 0,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                minWidth: 520,
            }}
        >
            <div style={{ display: 'flex', gap: 16, padding: 12 }}>
                {renderCalendar(leftMonth, 'left')}
                {renderCalendar(rightMonth, 'right')}
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderTop: `1px solid ${T.line}`,
                    background: T.bgSoft,
                }}
            >
                <span style={{ flex: 1, fontSize: 11, color: T.muted }}>{footerText}</span>
                {(startDate || endDate) && (
                    <button
                        type="button"
                        onClick={() => {
                            setStartDate(null);
                            setEndDate(null);
                            setHoverDate(null);
                            onClear?.();
                        }}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: T.muted,
                            cursor: 'pointer',
                            fontSize: 11,
                            padding: '2px 6px',
                            outline: 'none',
                        }}
                    >
                        Limpar
                    </button>
                )}
            </div>
        </div>
    );
}
